import { Github, Linkedin, Mail, Twitter, Wallet, Youtube } from "lucide-react";
import { useState } from "react";
import { Link, useHistory } from "react-router-dom";
import loadableComponent from "@loadable/component";
import { Newsletter } from "./Newsletter";
import { VestnLogo } from "./VestnLogo";
import { Button } from "./ui/button";

const ThirdWebLogin = loadableComponent(() => import("./Modals/thirdWebLogin"));

export const FooterNew = () => {
  const history = useHistory();
  const [showWalletGate, setShowWalletGate] = useState(false);

  const handleDashboardClick = (e: React.MouseEvent) => {
    const isAuth = !!localStorage.getItem("RwaAuthToken");
    if (!isAuth) {
      e.preventDefault();
      setShowWalletGate(true);
    }
  };

  return (
    <footer className="bg-card border-t border-border print:hidden">
      <div className="container mx-auto px-4 lg:px-8 py-12 lg:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-8 lg:gap-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <VestnLogo size="md" className="mb-4" />
            <p className="text-sm text-muted-foreground leading-relaxed mb-6 max-w-sm">
              The application layer for tokenization. Direct fractional ownership of institutional real-world assets through regulated SPVs.
            </p>
            <div className="flex items-center gap-3">
              <a
                href="https://twitter.com/vestn"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center text-muted-foreground hover:bg-primary/20 hover:text-primary transition-colors"
              >
                <Twitter className="w-5 h-5" />
              </a>
              <a
                href="https://linkedin.com/company/vestn"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center text-muted-foreground hover:bg-primary/20 hover:text-primary transition-colors"
              >
                <Linkedin className="w-5 h-5" />
              </a>
              <a
                href="https://github.com/vestn"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center text-muted-foreground hover:bg-primary/20 hover:text-primary transition-colors"
              >
                <Github className="w-5 h-5" />
              </a>
              <a
                href="https://www.youtube.com/@VESTN_io"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="VESTN on YouTube"
                className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center text-muted-foreground hover:bg-primary/20 hover:text-primary transition-colors"
              >
                <Youtube className="w-5 h-5" />
              </a>
              <a
                href="mailto:contact@vestn.io"
                className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center text-muted-foreground hover:bg-primary/20 hover:text-primary transition-colors"
              >
                <Mail className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Platform */}
          <div>
            <h4 className="font-semibold text-foreground mb-4">Platform</h4>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/primarymarketplace"
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  Primary Marketplace
                </Link>
              </li>
              <li>
                <Link
                  to="/secondary-marketplace"
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  Secondary Marketplace
                </Link>
              </li>
              <li>
                <Link
                  to="/rental"
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  Yield Assets
                </Link>
              </li>
              <li>
                <Link
                  to="/user/profile"
                  onClick={handleDashboardClick}
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  Dashboard
                </Link>
              </li>
              <li>
                <Link
                  to="/how-to-use"
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  How it Works
                </Link>
              </li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="font-semibold text-foreground mb-4">Company</h4>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/about"
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  About Us
                </Link>
              </li>
              <li>
                <Link
                  to="/careers"
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  Careers
                </Link>
              </li>
              <li>
                <Link
                  to="/press"
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  Press
                </Link>
              </li>
              <li>
                <Link
                  to="/partners"
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  Partners
                </Link>
              </li>
              <li>
                <Link
                  to="/contact"
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  Contact
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-semibold text-foreground mb-4">Legal</h4>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/privacy"
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link
                  to="/terms"
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  Terms of Service
                </Link>
              </li>
              <li>
                <Link
                  to="/risk-disclosure"
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  Risk Disclosure
                </Link>
              </li>
              <li>
                <Link
                  to="/regulatory-compliance"
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  Regulatory Compliance
                </Link>
              </li>
              <li>
                <Link
                  to="/cookies"
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  Cookie Policy
                </Link>
              </li>
            </ul>
          </div>

          {/* Newsletter */}
          <Newsletter variant="footer" />
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-border">
          <div className="flex flex-col lg:flex-row justify-between items-center gap-4">
            <p className="text-xs text-muted-foreground text-center lg:text-left">
              © 2026 VESTN Ltd. All rights reserved.{" "}
              <span className="text-vestn-pending font-semibold">IMPORTANT:</span>{" "}
              Informational preview only. Not investment advice. Capital at risk. Tokenized securities may be illiquid and unavailable for resale.
            </p>
            <div className="flex items-center gap-4">
              <span className="text-xs text-muted-foreground">
                Powered by Blockchain Technology
              </span>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                <span className="text-xs text-primary font-medium">
                  Platform Preview
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Connect Wallet Gate */}
      {showWalletGate && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          onClick={() => setShowWalletGate(false)}
        >
          <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" />
          <div
            className="relative z-10 w-full max-w-sm mx-4 p-8 rounded-2xl bg-card border border-white/[0.07] text-center shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-16 h-16 rounded-2xl bg-primary/20 flex items-center justify-center mx-auto mb-6">
              <Wallet className="w-8 h-8 text-primary" />
            </div>
            <h2 className="font-display text-2xl font-bold text-foreground mb-3">
              Connect Your Wallet
            </h2>
            <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
              Connect to view your live portfolio. The dashboard behind is a sample preview.
            </p>
            <ThirdWebLogin
              toggle={() => {
                setShowWalletGate(false);
                history.push("/user/profile");
              }}
              className="w-full"
            />
            <Button
              variant="ghost"
              size="sm"
              className="mt-3 w-full text-muted-foreground"
              onClick={() => setShowWalletGate(false)}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </footer>
  );
};
